import { Check } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { PASSWORD_MIN_LENGTH } from '@eldescuadre/contracts';

import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { AuthLayout } from './LoginPage';

export function RegisterPage(): React.JSX.Element {
  const status = useSession((s) => s.status);
  const signUp = useSession((s) => s.signUp);
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /**
   * Los requisitos de la contraseña, comprobados **mientras se escribe**.
   *
   * Antes había que enviar el formulario para descubrir qué faltaba, y encima salía
   * un aviso genérico («los datos enviados no son válidos») junto al mensaje concreto.
   * Dos mensajes para el mismo problema, y el genérico no dice nada. Ahora se ve el
   * requisito antes de enviar, y cuando falta algo se marca en el momento.
   */
  const requirements = [
    {
      label: `Al menos ${PASSWORD_MIN_LENGTH} caracteres`,
      met: password.length >= PASSWORD_MIN_LENGTH,
    },
    { label: 'Al menos una letra', met: /[a-zA-Z]/.test(password) },
    { label: 'Al menos un número', met: /[0-9]/.test(password) },
  ];
  const passwordReady = requirements.every((requirement) => requirement.met);

  /** Solo se enseña el aviso general cuando el error no es de un campo concreto. */
  const showGeneralError = error !== null && Object.keys(fieldErrors).length === 0;

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSending(true);
    try {
      await signUp({
        displayName: displayName.trim(),
        email: email.trim().toLowerCase(),
        password,
        acceptPrivacy: true as const,
      });
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.fields.length > 0) {
        setFieldErrors(
          Object.fromEntries(caught.fields.map((f) => [f.path, f.message])),
        );
        setError(caught.message);
      } else {
        setError(caught instanceof Error ? caught.message : 'No se pudo crear la cuenta');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout title="Crear tu cuenta" subtitle="Control total de tus gastos">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label className="ed-label" htmlFor="nombre">
            ¿Cómo te llamas?
          </label>
          <input
            id="nombre"
            name="name"
            type="text"
            autoComplete="name"
            required
            className="ed-input"
            placeholder="Tu nombre"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <FieldError message={fieldErrors['displayName']} />
        </div>

        <div>
          <label className="ed-label" htmlFor="correo">
            Correo electrónico
          </label>
          <input
            id="correo"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            className="ed-input"
            placeholder="tucorreo@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FieldError message={fieldErrors['email']} />
        </div>

        <div>
          <label className="ed-label" htmlFor="clave">
            Contraseña
          </label>
          <input
            id="clave"
            name="new-password"
            type="password"
            autoComplete="new-password"
            required
            className="ed-input"
            placeholder={`Al menos ${PASSWORD_MIN_LENGTH} caracteres`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* Los requisitos, visibles desde el principio y marcándose al escribirlos. */}
          <ul className="mt-1.5 space-y-0.5">
            {requirements.map((requirement) => (
              <li
                key={requirement.label}
                className={`flex items-center gap-1.5 text-xs ${
                  requirement.met ? 'text-ed-success' : 'text-ed-text-muted'
                }`}
              >
                {requirement.met ? (
                  <Check size={12} aria-hidden="true" />
                ) : (
                  <span className="inline-block h-3 w-3 rounded-full border border-current" aria-hidden="true" />
                )}
                <span>
                  {requirement.label}
                  <span className="sr-only">
                    {requirement.met ? ': cumplido' : ': pendiente'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <FieldError message={fieldErrors['password']} />
        </div>

        <label className="flex items-start gap-2 text-sm text-ed-text-silver">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-ed-bg-border bg-ed-bg-abyss"
            checked={acceptPrivacy}
            onChange={(e) => setAcceptPrivacy(e.target.checked)}
          />
          <span>
            Acepto la política de tratamiento de datos personales (Ley 1581 de Colombia). Tus
            finanzas quedan en tu propio servidor y no se comparten con terceros.
          </span>
        </label>
        <FieldError message={fieldErrors['acceptPrivacy']} />

        {showGeneralError && (
          <p
            role="alert"
            className="rounded-ed border border-ed-coral-deep/50 bg-ed-coral-deep/15 px-3 py-2 text-sm text-ed-coral-light"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          className="ed-btn-primary w-full"
          disabled={sending || !acceptPrivacy || !passwordReady}
        >
          {sending ? 'Creando…' : 'Crear mi cuenta'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-ed-text-muted">
        ¿Ya tienes cuenta?{' '}
        <Link to="/entrar" className="font-semibold text-ed-gold hover:underline">
          Entrar
        </Link>
      </p>
    </AuthLayout>
  );
}

function FieldError({ message }: { message?: string | undefined }): React.JSX.Element | null {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-ed-coral-light">
      {message}
    </p>
  );
}
